import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import {
	getConnectorAccountsSummary,
	ConnectorAccountSummary,
	DailyRows,
} from '../../Services/usageService';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface UsageDashboardProps {
	billingAccountId: string;
}

const UsageDashboard: React.FC<UsageDashboardProps> = ({ billingAccountId }) => {
	const [isPending, setIsPending] = useState(true);
	const [connectorAccounts, setConnectorAccounts] = useState<ConnectorAccountSummary[]>([]);
	const [totalAdAccounts, setTotalAdAccounts] = useState(0);
	const [rowsByDay, setRowsByDay] = useState<DailyRows[]>([]);
	const [totalActiveRowsLast30d, setTotalActiveRowsLast30d] = useState(0);

	const load = useCallback(async () => {
		setIsPending(true);
		try {
			const res = await getConnectorAccountsSummary(billingAccountId);
			setConnectorAccounts(res.accounts || []);
			setTotalAdAccounts(res.totalAdAccounts || 0);
			setRowsByDay(res.rowsByDay || []);
			setTotalActiveRowsLast30d(res.totalActiveRowsLast30d || 0);
		} catch (error) {
			console.error('Error fetching connector accounts summary:', error);
		} finally {
			setIsPending(false);
		}
	}, [billingAccountId]);

	useEffect(() => {
		load();
	}, [load]);

	const uniqueConnectorsCount = connectorAccounts.length;
	const uniqueProjectsCount = useMemo(
		() => new Set(connectorAccounts.filter((a) => a.projectFolderId).map((a) => a.projectFolderId)).size,
		[connectorAccounts]
	);

	const chartData = useMemo(() => {
		if (!rowsByDay.length) return null;
		return {
			labels: rowsByDay.map((d) => {
				const date = new Date(d.date);
				return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
			}),
			datasets: [
				{
					label: 'Inserted Rows (last 30 days)',
					data: rowsByDay.map((d) => d.totalActiveRows || 0),
					backgroundColor: 'rgba(255, 52, 15, 0.8)',
					hoverBackgroundColor: 'rgba(240, 26, 6, 1)',
					borderRadius: 2,
				},
			],
		};
	}, [rowsByDay]);

	const chartOptions = useMemo(
		() => ({
			responsive: true,
			plugins: {
				legend: { display: false },
			},
			scales: {
				x: { grid: { display: false } },
				y: { grid: { color: '#f3f4f6' }, border: { display: false } },
			},
		}),
		[]
	);

	if (isPending) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
				Inserted rows : 30 derniers jours · Connectors / Projects / Ad accounts : état actuel du BA
			</Typography>

			{/* Stats cards */}
			<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
				<Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
					<Typography variant="h4" fontWeight="bold">
						{uniqueConnectorsCount}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Connectors
					</Typography>
				</Box>
				<Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
					<Typography variant="h4" fontWeight="bold">
						{uniqueProjectsCount}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Projects
					</Typography>
				</Box>
				<Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
					<Typography variant="h4" fontWeight="bold">
						{totalActiveRowsLast30d.toLocaleString('en-US')}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Inserted rows (30d)
					</Typography>
				</Box>
				<Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
					<Typography variant="h4" fontWeight="bold">
						{totalAdAccounts.toLocaleString('en-US')}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Ad accounts
					</Typography>
				</Box>
			</Box>

			{chartData && (
				<Box sx={{ mb: 3 }}>
					<Bar data={chartData} options={chartOptions} />
				</Box>
			)}

			<Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
				Detail
			</Typography>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Connector</TableCell>
						<TableCell>Project</TableCell>
						<TableCell align="right">Ad accounts</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{connectorAccounts.map((a) => (
						<TableRow key={`${a.projectFolderId}-${a.sku}-${a.accountId}`}>
							<TableCell>{`${a.sku} / ${a.accountName || a.accountId}`}</TableCell>
							<TableCell>{a.projectName || a.projectFolderId}</TableCell>
							<TableCell align="right">{a.nbrAdAccounts}</TableCell>
						</TableRow>
					))}
					{connectorAccounts.length > 0 && (
						<TableRow>
							<TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
							<TableCell />
							<TableCell align="right" sx={{ fontWeight: 'bold' }}>
								{totalAdAccounts.toLocaleString('en-US')}
							</TableCell>
						</TableRow>
					)}
					{connectorAccounts.length === 0 && (
						<TableRow>
							<TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
								No connector
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</Box>
	);
};

export default UsageDashboard;
